import { useQuery } from "@apollo/client";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Warning from "@mui/icons-material/Warning";
import {
  Box,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { EncampyPortalLoginLauncher } from "components/Encampy/PortalLoginLauncher";
import { CredentialFieldsDisplay } from "components/PortalCredential/CredentialFieldsDisplay";
import { findAlternateId } from "encamp-shared/src/facilityAlternateId/findAlternateId";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { GetTierIiReportQuery } from "generated-graphql/graphql";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import pluralize from "pluralize";
import { GET_STEERS_CREDENTIALS_PAGE_DATA } from "routes/Staff/SteersCredentials/api";
import { CredentialVerification } from "./CredentialVerification";
import { FieldDisplay } from "./shared/FieldComponents";
import { useFulfillmentDetails } from "./useFulfillmentDetails";

const VerificationSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

type FacilityAlternateId = NonNullable<
  GetTierIiReportQuery["tierIIReport"]["facility"]["facilityAlternateIds"]
>[number];

type PortalCredentialItem = NonNullable<
  GetTierIiReportQuery["tierIIReport"]["facility"]["portalCredentials"]
>[number];

function portalTypeLabel(type: string | null | undefined): string {
  return `${prettyPrintEnumValue(type)} Portal`;
}

export function PortalCredentials() {
  const theme = useTheme();
  const alerts = useAlerts();
  const { data } = useFulfillmentDetails();
  const [isExpanded, setIsExpanded] = useLocalStorageState<boolean>(
    "fulfillment-state-portal-expanded",
    { defaultValue: true }
  );

  const facility = data?.tierIIReport.facility;
  const portalCredentialsList: PortalCredentialItem[] =
    facility && (facility.portalCredentials?.length ?? 0) > 0
      ? facility.portalCredentials
      : [];
  const facilityAlternateIds = facility?.facilityAlternateIds;
  const reportingYear = data?.tierIIReport.reportingYear;
  const reportKind = data?.tierIIReport.reportKind;

  const isTexas = facility?.state === "TX";

  const verificationStatusOnly = portalCredentialsList.some(
    (pc) => pc?.credential?.verificationStatus
  ) ? (
    <Stack spacing={1}>
      {portalCredentialsList.map(
        (pc) =>
          pc?.credential && (
            <FieldDisplay
              key={pc.type ?? "portal"}
              label={`${portalTypeLabel(pc.type)} verification:`}
              value={prettyPrintEnumValue(pc.credential.verificationStatus)}
              noWrap
            />
          )
      )}
    </Stack>
  ) : null;

  const hasCredsToShow =
    (isTexas && (facilityAlternateIds?.length ?? 0) > 0) ||
    portalCredentialsList.length > 0;

  if (!hasCredsToShow) {
    return null;
  }

  return (
    <Stack
      direction="column"
      component={Paper}
      elevation={0}
      spacing={2}
      padding={theme.spacing(2, 3, 2, 3)}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Typography variant="h6" data-testid="state-portal-title">
          {pluralize("Credential", portalCredentialsList.length)}
        </Typography>
        <IconButton size="small">
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {!isExpanded && verificationStatusOnly && (
        <Box sx={{ pl: 0 }}>{verificationStatusOnly}</Box>
      )}

      <Collapse in={isExpanded}>
        <Grid container spacing={3}>
          <Grid size={12}>
            {isTexas ? (
              <TexasCredentialsContent
                facilityAlternateIds={facilityAlternateIds ?? []}
                portalCredentialsList={portalCredentialsList}
                reportingYear={reportingYear}
                reportKind={reportKind}
              />
            ) : (
              <Stack spacing={3} divider={<Divider />}>
                {portalCredentialsList.map((pc, index) => {
                  const encampyDisabled =
                    !pc?.credential?.username || !pc?.credential?.password;
                  return (
                    <Box key={pc?.type ?? index}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ pb: 1 }}
                      >
                        <Typography variant="subtitle2" color="text.secondary">
                          {portalTypeLabel(pc?.type)}
                        </Typography>
                        <EncampyPortalLoginLauncher
                          portalCredential={pc}
                          facilityAlternateIds={facilityAlternateIds}
                          reportingYear={reportingYear}
                          reportKind={reportKind}
                          onSuccess={(message) => alerts.success(message)}
                          onError={(error) => alerts.error(error)}
                          size="small"
                          disabled={encampyDisabled}
                          disabledReason={
                            encampyDisabled
                              ? "Portal credentials are required for portal login"
                              : undefined
                          }
                        />
                      </Stack>
                      <CredentialFieldsDisplay
                        credential={pc?.credential ?? undefined}
                        portalUrl={pc?.url ?? undefined}
                        showCombinedInvoicing={facility?.state === "WV"}
                      />
                      {pc?.credential && (
                        <VerificationSection sx={{ mt: 2 }}>
                          <CredentialVerification
                            credential={pc.credential}
                            facilityId={facility?.id ?? ""}
                          />
                        </VerificationSection>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Grid>
        </Grid>
      </Collapse>
    </Stack>
  );
}

/** For Texas, displays
 * - Error if the facility does not have a CN
 * - Number of associated credentials for a given STEERS CN
 * - Link to the STEERS Credentials page for the given CN
 * - "Open with Encampy" button (TX credentials are managed via STEERS, so no
 *   per-credential button is shown in the body like non-TX states)
 *  */
function TexasCredentialsContent({
  facilityAlternateIds,
  portalCredentialsList,
  reportingYear,
  reportKind,
}: {
  facilityAlternateIds: FacilityAlternateId[];
  portalCredentialsList: PortalCredentialItem[];
  reportingYear: number | undefined;
  reportKind: GetTierIiReportQuery["tierIIReport"]["reportKind"] | undefined;
}) {
  const alerts = useAlerts();
  const steersCn = findAlternateId(facilityAlternateIds, ["STEERS_CN"])?.value;

  const { data: steersData } = useQuery(GET_STEERS_CREDENTIALS_PAGE_DATA, {
    variables: { cn: steersCn },
    skip: !steersCn,
  });

  // When filtered by CN, there will be at most one mapping
  const cnMapping = steersData?.steersCredentialsPageData.mappings[0];
  const credentialCount = cnMapping?.credentialCount ?? 0;

  // Pick the first available credential for the Encampy launcher
  const portalCredential = portalCredentialsList[0];
  const encampyDisabled =
    !portalCredential?.credential?.username ||
    !portalCredential?.credential?.password;

  if (!steersCn) {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Warning color="warning" sx={{ fontSize: 18 }} />
        <Typography variant="body2" color="text.secondary">
          Facility must have a STEERS CN for credential associations
        </Typography>
      </Stack>
    );
  }

  return (
    <Box>
      <Stack alignItems="end" pb={1}>
        {portalCredential && (
          <EncampyPortalLoginLauncher
            portalCredential={portalCredential}
            facilityAlternateIds={facilityAlternateIds}
            reportingYear={reportingYear}
            reportKind={reportKind}
            onSuccess={(message) => alerts.success(message)}
            onError={(error) => alerts.error(error)}
            size="small"
            disabled={encampyDisabled}
            disabledReason={
              encampyDisabled
                ? "Portal credentials are required for portal login"
                : undefined
            }
          />
        )}
      </Stack>
      <Link
        href={`/staff/steers-credentials?search=${encodeURIComponent(
          steersCn
        )}&associatedCredentials=true`}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
      >
        Manage Texas STEERS credential associations
        <OpenInNew sx={{ fontSize: 14 }} />
      </Link>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {credentialCount === 0 && (
          <Warning color="warning" sx={{ fontSize: 14 }} />
        )}
        <Typography
          variant="caption"
          color={credentialCount === 0 ? "warning.main" : "text.secondary"}
        >
          {credentialCount} credential{credentialCount !== 1 ? "s" : ""}{" "}
          associated with {steersCn}
        </Typography>
      </Stack>
    </Box>
  );
}
